/**
 * lib/render-policy/review-topicality.ts — separate a review about the
 * PRODUCT from one about the RETAILER purchase experience.
 *
 * The Internet's-Verdict highlight cards ("What owners love" / "The main
 * complaint") must quote the appliance, not the store. Owner report
 * 2026-07-08: a 1★ AJ Madison delivery rant ("purely atrocious… purchase and
 * delivery turned into weeks of lies") surfaced as "the main complaint" about
 * an LG refrigerator. Those reviews are real and stay in the full reviews
 * list — they just can't represent the product in a curated highlight.
 *
 * Heuristic, deliberately conservative (only the full reviews list is the
 * source of truth; a wrong exclusion here just picks a different quote):
 * flag as retailer-experience when the text leans on store/logistics
 * vocabulary (delivery, customer service, refund, a named retailer) WITHOUT
 * enough product vocabulary to counterbalance it.
 */

const RETAILER_NAMES =
  /\b(aj ?madison|abt|best ?buy|home ?depot|lowe'?s|wayfair|costco|walmart|appliances? ?connection|us ?appliance|nfm|nebraska furniture|the store|this store|their store)\b/i;

// Purchase / fulfillment / service vocabulary — the "buying experience", not
// the appliance. Word-boundaried so "cooler" doesn't trip "cool".
const LOGISTICS =
  /\b(deliver(?:y|ed|ies|ing)?|shipp(?:ing|ed)|freight|customer service|sales ?(?:person|rep|people|man)|warehouse|order(?:ed|ing|s)?|return(?:ed|ing|s)?|refund(?:ed|s)?|exchange|installer|installation crew|schedul(?:e|ed|ing)|reschedul(?:e|ed)|on hold|representative|call(?:ed)? (?:them|customer|support)|runaround|restocking fee|purchase experience|buying experience|the company|their service)\b/i;

// Appliance / feature vocabulary — evidence the review is about the product.
const PRODUCT_TERMS =
  /\b(fridge|refrigerat\w*|freezer|cool(?:s|ing|er)?|cold|temperatur\w*|ice ?maker|ice|shel(?:f|ves)|drawer|crisper|bin|door|compressor|quiet|noise|noisy|loud|hum(?:s|ming)?|spac(?:e|ious)|capacity|cu\.? ?ft|energy|efficien\w*|water (?:filter|dispenser|line)|led|light(?:ing|s)?|finish|stainless|fingerprint|counter.?depth|humidity|frost|defrost|storage|freeze[sr]?|food|shelving)\b/i;

function countMatches(text: string, re: RegExp): number {
  const g = new RegExp(re.source, re.flags.includes('g') ? re.flags : re.flags + 'g');
  return (text.match(g) ?? []).length;
}

/**
 * True when the review reads as a store/purchase-experience report rather
 * than a product review. Empty/near-empty bodies are NOT flagged (nothing to
 * judge — the caller's length gate handles those).
 */
export function isRetailerExperienceReview(body: string | null | undefined): boolean {
  if (!body) return false;
  const text = body.toLowerCase();

  const hasRetailerName = RETAILER_NAMES.test(text);
  const logisticsHits = countMatches(text, LOGISTICS);
  const productHits = countMatches(text, PRODUCT_TERMS);

  // Clearly product-focused → keep, regardless of an incidental "delivery".
  if (productHits >= 3) return false;

  // Store-experience signal: a named retailer plus any logistics word, or a
  // pile of logistics vocabulary on its own — and not enough product talk to
  // outweigh it.
  const storeSignal = (hasRetailerName && logisticsHits >= 1) || logisticsHits >= 2;
  return storeSignal && productHits <= 1;
}

/** Convenience inverse for `.filter(...)`. */
export function isProductReview(body: string | null | undefined): boolean {
  return !isRetailerExperienceReview(body);
}
